import { THEME } from "@/config/theme";
import type { ApiMcpKey } from "@/services/mcpKeyService";

interface Props {
  keys: ApiMcpKey[];
  onRevoke: (key: ApiMcpKey) => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** A key is dead if it was revoked or its expiry has passed. */
function statusOf(k: ApiMcpKey): "active" | "revoked" | "expired" {
  if (k.revokedAt) return "revoked";
  if (k.expiresAt && new Date(k.expiresAt).getTime() <= Date.now()) return "expired";
  return "active";
}

export default function KeyList({ keys, onRevoke }: Props) {
  if (keys.length === 0) {
    return (
      <p className={`${THEME.mcpPage.hint} py-6 text-center`}>
        No tokens yet. Create one to connect an MCP client.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left">
            <th className={`${THEME.pageCard.label} ${THEME.mcpPage.columnLabel} py-2 pr-3`}>Name</th>
            <th className={`${THEME.pageCard.label} ${THEME.mcpPage.columnLabel} py-2 pr-3`}>Token</th>
            <th className={`${THEME.pageCard.label} ${THEME.mcpPage.columnLabel} py-2 pr-3`}>Created</th>
            <th className={`${THEME.pageCard.label} ${THEME.mcpPage.columnLabel} py-2 pr-3`}>Last used</th>
            <th className={`${THEME.pageCard.label} ${THEME.mcpPage.columnLabel} py-2 pr-3`}>Expires</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const status = statusOf(k);
            const dead = status !== "active";
            return (
              <tr
                key={k.id}
                data-testid="key-row"
                className={`${THEME.panel.borderTop} border-t ${dead ? "opacity-50" : ""}`}
              >
                <td className={`py-2.5 pr-3 ${THEME.mcpPage.cell} font-medium`}>{k.name}</td>
                <td className={`py-2.5 pr-3 font-mono ${THEME.mcpPage.cellMuted}`}>
                  tai_{k.keyPrefix}…
                </td>
                <td className={`py-2.5 pr-3 ${THEME.mcpPage.cellMuted}`}>{formatDate(k.createdAt)}</td>
                <td className={`py-2.5 pr-3 ${THEME.mcpPage.cellMuted}`}>
                  {k.lastUsedAt ? formatDate(k.lastUsedAt) : "Never"}
                </td>
                <td className={`py-2.5 pr-3 ${THEME.mcpPage.cellMuted}`}>
                  {k.expiresAt ? formatDate(k.expiresAt) : "Never"}
                </td>
                <td className="py-2.5 text-right">
                  {dead ? (
                    <span className={THEME.mcpPage.cellMuted}>{status}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRevoke(k)}
                      className={`px-2.5 py-1 text-[13px] font-medium rounded-md ${THEME.sidebar.dangerHover} transition-colors`}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
